import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type MutableRefObject,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import {
  selectScheduleColumnWidth,
  selectScheduleItemScaleMultiplier,
  selectScheduleConstraints,
} from '../../../../../features/schedule/scheduleSelectors';
import {
  computeScheduleZoomScrollAnchor,
  resolveScheduleScrollContainer,
} from '../../../../../features/schedule/scheduleZoomAnchor';
import {
  applyLiveScheduleZoomCss,
  getScheduleZoomCanvasParams,
} from './scheduleZoomCanvasRegistry';
import type { ScheduleZoomFloors } from '../../../../../features/schedule/scheduleZoomBounds';
import type { ScheduleConflict } from '../../../../../features/schedule/scheduleConflictDetector';
import type { ScheduleLeftRailTab } from './ScheduleLeftRail';
import { ScheduleDnDShell } from './ScheduleDnDShell';
import { ScheduleZoomProvider } from './ScheduleZoomContext';
import {
  isScheduleHistoryKeyTarget,
  isScheduleZoomInKey,
  isScheduleZoomOutKey,
} from './scheduleLayoutHooks';
import { useScheduleZoomGesture } from './useScheduleZoomGesture';

type ScheduleZoomRootProps = {
  scheduleZoomFloors: ScheduleZoomFloors;
  selectedDayKey: number;
  onDayZoomFloorsChange: (dayKey: number, floors: ScheduleZoomFloors) => void;
  isMobile: boolean;
  lastGridPointerRef: MutableRefObject<{ clientX: number; clientY: number } | null>;
  clearPlacementRef: MutableRefObject<(() => void) | null>;
  showScheduleConflicts: boolean;
  conflictMatchIdList: number[];
  blockInvalidPlacement: boolean;
  leftRailTab: ScheduleLeftRailTab;
  onLeftRailTabChange: (tab: ScheduleLeftRailTab) => void;
  onSelectedDayKeyChange: (dayKey: number) => void;
  focusedConflictMatchId: number | null;
  focusedConflictPeerIds: number[];
  conflictFocusPulseGeneration: number;
  onFocusConflictOnGrid: (conflict: ScheduleConflict) => void;
  onViewConflictMatch: (matchId: number) => void;
};

export function ScheduleZoomRoot({
  scheduleZoomFloors,
  selectedDayKey,
  onDayZoomFloorsChange,
  isMobile,
  lastGridPointerRef,
  clearPlacementRef,
  showScheduleConflicts,
  conflictMatchIdList,
  blockInvalidPlacement,
  leftRailTab,
  onLeftRailTabChange,
  onSelectedDayKeyChange,
  focusedConflictMatchId,
  focusedConflictPeerIds,
  conflictFocusPulseGeneration,
  onFocusConflictOnGrid,
  onViewConflictMatch,
}: ScheduleZoomRootProps) {
  const dispatch = useAppDispatch();
  const scheduleItemScaleMultiplier = useAppSelector(selectScheduleItemScaleMultiplier);
  const scheduleColumnWidth = useAppSelector(selectScheduleColumnWidth);
  const scheduleConstraints = useAppSelector(selectScheduleConstraints);

  const lastAnchorRef = useRef<{
    scrollEl: HTMLElement;
    scrollTop: number;
    scrollLeft: number;
    scale: number;
    width: number;
  } | null>(null);

  const captureScheduleZoomAnchor = useCallback(
    (
      clientX: number,
      clientY: number,
      nextScale: number,
      nextWidth: number,
      oldScale: number,
      oldWidth: number
    ) => {
      if (nextScale === oldScale && nextWidth === oldWidth) {
        return;
      }
      const scrollEl = resolveScheduleScrollContainer(clientX, clientY);
      if (!scrollEl) return;
      const canvasEl = scrollEl.querySelector<HTMLElement>('.scheduleVirtualCanvas');
      if (!canvasEl) return;
      const canvasParams = getScheduleZoomCanvasParams(canvasEl);
      if (!canvasParams) return;
      const rect = scrollEl.getBoundingClientRect();
      const anchor = computeScheduleZoomScrollAnchor({
        pointerX: clientX - rect.left,
        pointerY: clientY - rect.top,
        scrollTop: scrollEl.scrollTop,
        scrollLeft: scrollEl.scrollLeft,
        oldScale,
        newScale: nextScale,
        oldColumnWidth: oldWidth,
        newColumnWidth: nextWidth,
        courtCount: canvasParams.courtCount,
        slotCount: canvasParams.slotCount,
        viewportWidth: scrollEl.clientWidth,
        viewportHeight: scrollEl.clientHeight,
      });
      scrollEl.scrollTop = anchor.scrollTop;
      scrollEl.scrollLeft = anchor.scrollLeft;
      lastAnchorRef.current = {
        scrollEl,
        scrollTop: anchor.scrollTop,
        scrollLeft: anchor.scrollLeft,
        scale: nextScale,
        width: nextWidth,
      };
    },
    []
  );

  const zoomGesture = useScheduleZoomGesture({
    reduxScale: scheduleItemScaleMultiplier,
    reduxColumnWidth: scheduleColumnWidth,
    scheduleZoomFloors,
    dispatch,
    captureZoomAnchor: captureScheduleZoomAnchor,
  });

  const zoomContextValue = useMemo(
    () => ({
      scale: zoomGesture.scale,
      columnWidth: zoomGesture.columnWidth,
      isGesturing: zoomGesture.isGesturing,
    }),
    [zoomGesture.scale, zoomGesture.columnWidth, zoomGesture.isGesturing]
  );

  const applyZoomStepRef = useRef(zoomGesture.applyZoomStep);
  applyZoomStepRef.current = zoomGesture.applyZoomStep;

  useLayoutEffect(() => {
    if (!zoomGesture.isGesturing) return;
    const a = lastAnchorRef.current;
    if (!a) return;
    applyLiveScheduleZoomCss(a.scale, a.width);
    a.scrollEl.scrollTop = a.scrollTop;
    a.scrollEl.scrollLeft = a.scrollLeft;
  }, [zoomGesture.isGesturing]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isScheduleHistoryKeyTarget(e.target)) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      if (!scheduleConstraints) return;
      if (isScheduleZoomInKey(e)) {
        e.preventDefault();
        const ptr = lastGridPointerRef.current;
        applyZoomStepRef.current(true, ptr ?? undefined);
        return;
      }
      if (isScheduleZoomOutKey(e)) {
        e.preventDefault();
        const ptr = lastGridPointerRef.current;
        applyZoomStepRef.current(false, ptr ?? undefined);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [scheduleConstraints, lastGridPointerRef]);

  return (
    <ScheduleZoomProvider value={zoomContextValue}>
      <div className="schedule-page__body-fill">
        <ScheduleDnDShell
          selectedDayKey={selectedDayKey}
          queueWheelZoom={zoomGesture.queueWheelZoom}
          onDayZoomFloorsChange={onDayZoomFloorsChange}
          isMobile={isMobile}
          lastGridPointerRef={lastGridPointerRef}
          clearPlacementRef={clearPlacementRef}
          showScheduleConflicts={showScheduleConflicts}
          conflictMatchIdList={conflictMatchIdList}
          blockInvalidPlacement={blockInvalidPlacement}
          leftRailTab={leftRailTab}
          onLeftRailTabChange={onLeftRailTabChange}
          onSelectedDayKeyChange={onSelectedDayKeyChange}
          focusedConflictMatchId={focusedConflictMatchId}
          focusedConflictPeerIds={focusedConflictPeerIds}
          conflictFocusPulseGeneration={conflictFocusPulseGeneration}
          onFocusConflictOnGrid={onFocusConflictOnGrid}
          onViewConflictMatch={onViewConflictMatch}
        />
      </div>
    </ScheduleZoomProvider>
  );
}
